import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Cancel as CancelIcon } from '@mui/icons-material';
import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';
import { usePersonaGetPersonaDeletionValidation } from '@/api-gen/api';
import type { PersonaDeletionValidationDto } from '@/api-gen/api.schemas';
import PersonaScenariosDisplay from './PersonaScenariosDisplay';

interface DeletePersonaDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  personaId: number;
  personaName?: string;
  userRole: 'Admin' | 'SuperAdmin';
}

const DeletePersonaDialog: React.FC<DeletePersonaDialogProps> = ({
  open,
  onClose,
  onConfirm,
  personaId,
  personaName,
  userRole
}) => {
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);

  // Use the generated API hook for validation
  const { 
    data: apiResponse, 
    isLoading: loading, 
    error,
    refetch 
  } = usePersonaGetPersonaDeletionValidation<PersonaDeletionValidationDto, Error>(personaId, {
    query: {
      enabled: open && !!personaId,
      refetchOnMount: 'always',
      staleTime: 0
    }
  });

  // Extract validation data from the API response
  const validation = ((apiResponse as any)?.data || apiResponse) as PersonaDeletionValidationDto | undefined;

  const canDelete = validation?.canDelete === true;
  const hasScenarios = validation?.scenariosUsingPersona && validation.scenariosUsingPersona.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.title))}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" alignItems="center" gap={2} py={1}>
            <CircularProgress size={20} />
            <Typography>{translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.loading))}</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || 'Failed to load validation'}
          </Alert>
        )}

        {!loading && !error && validation && (
          <>
            {!canDelete ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Box>
                    <Typography fontWeight={600} gutterBottom>
                      {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.inUseTitle))}
                    </Typography>
                    {validation.isSharedWithOtherOrganizations && userRole === 'Admin' && !validation.scenariosUsingPersona?.length ? (
                      <Typography sx={{ mt: 1 }}>
                        {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.reasonSharedAdmin))}
                      </Typography>
                    ) : null}
                  </Box>
                </Alert>

                {hasScenarios && (
                  <Box sx={{ mt: 2 }}>
                    <PersonaScenariosDisplay personaId={personaId} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography fontWeight={600}>
                    {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.readyTitle))}
                  </Typography>
                  <Typography>{translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.noUsage))}</Typography>
                </Alert>
                <Typography>
                  {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.confirmationMessage))}{' '}
                  <strong>{personaName || translate(nameof.full<I18nPersonasNs>((n) => n.card.unnamedPersona))}</strong>?
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          startIcon={<DeleteIcon />}
          disabled={!validation || !validation.canDelete || loading}
        >
          {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.delete))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePersonaDialog;
